import { CurrencyCodeSchema, majorAmountToMinor, } from "../accounting/money.js";
/** The one `ExtractionKind` the bill/receipt scan flow uses — mirrors
 * `VACCINATION_SCAN_KINDS` in `vaccination-scan-extraction.ts`. */
export const BILL_SCAN_KINDS = ["BILL_RECEIPT"];
export function isBillScanKind(kind) {
    return BILL_SCAN_KINDS.includes(kind);
}
/**
 * Every field the bill/receipt scan can prefill. `docs/products/stable.md`
 * "Accounting" names vendor, date, and description explicitly ("AI
 * extraction returns vendor, date, and description and auto-categorizes
 * it"); this module additionally attempts `amountMinor`/`currency`
 * (deliberate interpretation beyond the spec's literal three-field list —
 * an Accounting bill without an amount is not useful bookkeeping, and
 * "every extracted field remains editable" already establishes the
 * governing principle for any field a scan can find, not just the three
 * named ones; see the Phase 7 completion report). `category` is the
 * "auto-categorizes it" field, resolved the same way `vaccineType` is in
 * `vaccination-scan-extraction.ts` — the model's free-text guess normalized
 * against the Stable's actually configured category codes, never accepted
 * as arbitrary text.
 */
export const BILL_SCAN_FIELD_NAMES = [
    "vendor",
    "billDate",
    "description",
    "category",
    "currency",
    "amountMinor",
];
/** Fields the confirm screen must block save on until present — vendor,
 * date, category, and amount are the facts that make a bill a recorded
 * expense; description is optional/advisory. Currency is never "missing":
 * it always resolves to a value (the model's guess, or the Stable's
 * default AED-at-launch currency), so it is not a blocking field — see
 * {@link buildBillScanFields}. */
export const BILL_SCAN_COMPULSORY_FIELD_NAMES = [
    "vendor",
    "billDate",
    "category",
    "amountMinor",
];
const NO_VALUE = { value: null, confidence: null };
const LOCAL_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
const RAW_AMOUNT_PATTERN = /^\d+(?:\.\d+)?$/;
function normalizeCategory(raw, knownCategoryCodes) {
    const upper = raw.toUpperCase().replace(/[\s-]+/g, "_");
    return knownCategoryCodes.has(upper) ? upper : null;
}
function normalizeCurrency(raw, defaultCurrency) {
    const parsed = CurrencyCodeSchema.safeParse(raw);
    return parsed.success
        ? { value: parsed.data, wasGuessed: true }
        : { value: defaultCurrency, wasGuessed: false };
}
/**
 * Builds the confirm-screen draft fields from a provider's raw guesses —
 * mirrors `buildVaccinationScanFields`. Needs the Stable's currently
 * configured expense-category codes (to normalize `category` against, "a
 * configuration lookup value, not a fixed enum" — same reasoning as
 * `vaccineType`) and its default currency (AED at launch,
 * `docs/products/stable.md`/`COMPANY.md` "Target Market": "the UI only
 * exposes AED ... at launch") to resolve `amountMinor`, since converting a
 * major-unit guess like "45.50" into minor units requires knowing the
 * currency's decimal precision first ({@link majorAmountToMinor}).
 */
export function buildBillScanFields(raw, knownCategoryCodes, defaultCurrency) {
    const result = {};
    for (const field of ["vendor", "description"]) {
        const rawField = raw[field];
        const trimmed = rawField?.value?.trim();
        result[field] =
            trimmed === undefined || trimmed.length === 0
                ? NO_VALUE
                : { value: trimmed, confidence: rawField?.confidence ?? null };
    }
    const rawDate = raw.billDate?.value?.trim();
    result.billDate =
        rawDate !== undefined && LOCAL_DATE_PATTERN.test(rawDate)
            ? { value: rawDate, confidence: raw.billDate?.confidence ?? null }
            : NO_VALUE;
    const rawCategory = raw.category?.value?.trim();
    const normalizedCategory = rawCategory === undefined || rawCategory.length === 0
        ? null
        : normalizeCategory(rawCategory, knownCategoryCodes);
    result.category =
        normalizedCategory === null
            ? NO_VALUE
            : {
                value: normalizedCategory,
                confidence: raw.category?.confidence ?? null,
            };
    const rawCurrency = raw.currency?.value?.trim();
    const currency = rawCurrency === undefined || rawCurrency.length === 0
        ? { value: defaultCurrency, wasGuessed: false }
        : normalizeCurrency(rawCurrency, defaultCurrency);
    // Currency always resolves to a concrete value (the guess, or the
    // Stable's default) so the confirm screen never shows it as missing —
    // `wasGuessed` only affects the reported confidence, not whether a value
    // is present.
    result.currency = {
        value: currency.value,
        confidence: currency.wasGuessed ? (raw.currency?.confidence ?? null) : null,
    };
    const rawAmount = raw.amountMinor?.value?.trim();
    const amountMinor = rawAmount !== undefined && RAW_AMOUNT_PATTERN.test(rawAmount)
        ? majorAmountToMinor(rawAmount, currency.value)
        : null;
    result.amountMinor =
        amountMinor === null
            ? NO_VALUE
            : { value: amountMinor, confidence: raw.amountMinor?.confidence ?? null };
    return result;
}
export function missingCompulsoryBillScanFields(fields) {
    return BILL_SCAN_COMPULSORY_FIELD_NAMES.filter((field) => fields[field].value === null);
}
